import React, { FormEvent, useState } from "react";
import {
  HiCheck,
  HiCheckCircle,
  HiDuplicate,
  HiPlay,
  HiSave,
  HiTrash,
  HiX,
  HiXCircle,
} from "react-icons/hi";
import { AgentEvaluationPersonaType } from "../../../services/api";
import { ChatTranscript } from "../../Common/ChatTranscript";
import { EmptyDetail } from "./EmptyDetail";
import {
  callDirectionOptions,
  formatPercent,
  getStatusClasses,
  personaTypeOptions,
  scenarioFormDataToPayload,
  shortId,
  splitCommaList,
  splitLines,
  statusLabel,
} from "./shared";
import { UseAgentTestLab } from "./useAgentTestLab";

interface ScenarioEditorProps {
  lab: UseAgentTestLab;
}

const SUBTABS: { id: "definition" | "run" | "verdict"; label: string }[] = [
  { id: "definition", label: "Definition" },
  { id: "run", label: "Latest run" },
  { id: "verdict", label: "Verdict" },
];

export const ScenarioEditor: React.FC<ScenarioEditorProps> = ({ lab }) => {
  const scenario = lab.selectedScenario;

  if (!scenario) return <EmptyDetail />;

  const result = scenario.latest_result;
  const verdictBadge = result ? (
    <span
      className={`px-2 py-0.5 rounded-full border text-xs capitalize ${getStatusClasses(
        result.status
      )}`}
    >
      {statusLabel(result.status)}
    </span>
  ) : null;

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-[var(--border)] flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-[var(--foreground-muted)]">
            Scenario {shortId(scenario.id)} · v{scenario.version}
          </div>
          <h3 className="text-lg font-medium truncate mt-0.5">
            {scenario.title || "Untitled scenario"}
          </h3>
        </div>
        {verdictBadge}
      </div>

      <div className="px-5 border-b border-[var(--border)] flex items-end gap-1">
        {SUBTABS.map((tab) => {
          const active = lab.editorSubTab === tab.id;
          const disabled = (tab.id === "run" || tab.id === "verdict") && !result;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={disabled}
              onClick={() => lab.setEditorSubTab(tab.id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors disabled:opacity-40 disabled:cursor-default ${
                active
                  ? "border-[var(--accent)] text-[var(--foreground)]"
                  : "border-transparent text-[var(--foreground-muted)] hover:text-[var(--foreground)]"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto">
        {lab.editorSubTab === "definition" && (
          <DefinitionTab
            key={`${scenario.id}-${scenario.version}`}
            lab={lab}
          />
        )}
        {lab.editorSubTab === "run" && <RunTab lab={lab} />}
        {lab.editorSubTab === "verdict" && <VerdictTab lab={lab} />}
      </div>
    </div>
  );
};

const DefinitionTab: React.FC<{ lab: UseAgentTestLab }> = ({ lab }) => {
  const scenario = lab.selectedScenario!;
  const fieldId = (field: string) => `scenario-${scenario.id}-${field}`;
  const callDirection = scenario.call_direction ?? "incoming";
  const startingMessageLabel =
    callDirection === "outbound" ? "Agent opening message" : "User first message";
  const [listDrafts, setListDrafts] = useState(() => ({
    languages: scenario.languages.join(", "),
    passCriteria: scenario.pass_criteria.join("\n"),
    failureTraps: scenario.failure_traps.join("\n"),
  }));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    lab.handleSaveScenario(
      scenarioFormDataToPayload(new FormData(event.currentTarget))
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="px-5 py-5 space-y-4 flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor={fieldId("title")}
              className="block text-sm font-medium mb-1"
            >
              Title
            </label>
            <input
              id={fieldId("title")}
              name="title"
              value={scenario.title}
              onChange={(event) =>
                lab.updateScenarioLocal(scenario.id, { title: event.target.value })
              }
              className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
            />
          </div>
          <div>
            <label
              htmlFor={fieldId("persona-name")}
              className="block text-sm font-medium mb-1"
            >
              Persona name
            </label>
            <input
              id={fieldId("persona-name")}
              name="persona_name"
              value={scenario.persona_name}
              onChange={(event) =>
                lab.updateScenarioLocal(scenario.id, {
                  persona_name: event.target.value,
                })
              }
              className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
            />
          </div>
          <div>
            <label
              htmlFor={fieldId("persona-type")}
              className="block text-sm font-medium mb-1"
            >
              Persona type
            </label>
            <select
              id={fieldId("persona-type")}
              name="persona_type"
              value={scenario.persona_type}
              onChange={(event) =>
                lab.updateScenarioLocal(scenario.id, {
                  persona_type: event.target.value as AgentEvaluationPersonaType,
                })
              }
              className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
            >
              {personaTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor={fieldId("languages")}
              className="block text-sm font-medium mb-1"
            >
              Languages
            </label>
            <input
              id={fieldId("languages")}
              name="languages"
              value={listDrafts.languages}
              onChange={(event) => {
                const nextValue = event.target.value;
                setListDrafts((current) => ({
                  ...current,
                  languages: nextValue,
                }));
                lab.updateScenarioLocal(scenario.id, {
                  languages: splitCommaList(nextValue),
                });
              }}
              className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
            />
          </div>
        </div>

        <label
          htmlFor={fieldId("language-switching")}
          className="inline-flex items-center gap-2 text-sm"
        >
          <input
            id={fieldId("language-switching")}
            name="language_switching"
            type="checkbox"
            checked={scenario.language_switching}
            onChange={(event) =>
              lab.updateScenarioLocal(scenario.id, {
                language_switching: event.target.checked,
              })
            }
            className="h-4 w-4 accent-[var(--accent)]"
          />
          Expects language switching inside one conversation
        </label>

        <div>
          <label className="block text-sm font-medium mb-1">Call direction</label>
          <input type="hidden" name="call_direction" value={callDirection} />
          <div className="inline-flex rounded-md border border-[var(--border)] overflow-hidden">
            {callDirectionOptions.map((option) => {
              const active = callDirection === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() =>
                    lab.updateScenarioLocal(scenario.id, {
                      call_direction: option.value,
                    })
                  }
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    active
                      ? "bg-[var(--accent)] text-[var(--accent-foreground)]"
                      : "bg-transparent text-[var(--foreground)] hover:bg-[var(--muted)]"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor={fieldId("user-goal")}
              className="block text-sm font-medium mb-1"
            >
              User goal
            </label>
            <textarea
              id={fieldId("user-goal")}
              name="user_goal"
              rows={3}
              value={scenario.user_goal}
              onChange={(event) =>
                lab.updateScenarioLocal(scenario.id, {
                  user_goal: event.target.value,
                })
              }
              className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
            />
          </div>
          <div>
            <label
              htmlFor={fieldId("starting-message")}
              className="block text-sm font-medium mb-1"
            >
              {startingMessageLabel}
            </label>
            <textarea
              id={fieldId("starting-message")}
              name="starting_message"
              rows={3}
              value={scenario.starting_message}
              onChange={(event) =>
                lab.updateScenarioLocal(scenario.id, {
                  starting_message: event.target.value,
                })
              }
              className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
            />
          </div>
          <div>
            <label
              htmlFor={fieldId("expected-behavior")}
              className="block text-sm font-medium mb-1"
            >
              Expected behavior
            </label>
            <textarea
              id={fieldId("expected-behavior")}
              name="expected_behavior"
              rows={4}
              value={scenario.expected_behavior}
              onChange={(event) =>
                lab.updateScenarioLocal(scenario.id, {
                  expected_behavior: event.target.value,
                })
              }
              className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
            />
          </div>
          <div>
            <label
              htmlFor={fieldId("pass-criteria")}
              className="block text-sm font-medium mb-1"
            >
              Pass criteria (one per line)
            </label>
            <textarea
              id={fieldId("pass-criteria")}
              name="pass_criteria"
              rows={4}
              value={listDrafts.passCriteria}
              onChange={(event) => {
                const nextValue = event.target.value;
                setListDrafts((current) => ({
                  ...current,
                  passCriteria: nextValue,
                }));
                lab.updateScenarioLocal(scenario.id, {
                  pass_criteria: splitLines(nextValue),
                });
              }}
              className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
            />
          </div>
          <div className="lg:col-span-2">
            <label
              htmlFor={fieldId("failure-traps")}
              className="block text-sm font-medium mb-1"
            >
              Failure traps (one per line)
            </label>
            <textarea
              id={fieldId("failure-traps")}
              name="failure_traps"
              rows={3}
              value={listDrafts.failureTraps}
              onChange={(event) => {
                const nextValue = event.target.value;
                setListDrafts((current) => ({
                  ...current,
                  failureTraps: nextValue,
                }));
                lab.updateScenarioLocal(scenario.id, {
                  failure_traps: splitLines(nextValue),
                });
              }}
              className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 px-5 py-3 border-t border-[var(--border)] bg-[var(--card)]/95 backdrop-blur flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={lab.isSavingScenario}
          className="px-3 py-1.5 text-sm bg-[var(--accent)] text-[var(--accent-foreground)] rounded-md hover:bg-[var(--accent-hover)] disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <HiSave className="w-4 h-4" />
          {lab.isSavingScenario ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={lab.handleDuplicateScenario}
          className="px-3 py-1.5 text-sm rounded-md hover:bg-[var(--muted)] text-[var(--foreground-muted)] inline-flex items-center gap-1.5"
        >
          <HiDuplicate className="w-4 h-4" />
          Duplicate
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={lab.handleDeleteScenario}
          className="px-3 py-1.5 text-sm rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600 inline-flex items-center gap-1.5"
        >
          <HiTrash className="w-4 h-4" />
          Delete
        </button>
      </div>
    </form>
  );
};

const RunTab: React.FC<{ lab: UseAgentTestLab }> = ({ lab }) => {
  const scenario = lab.selectedScenario!;
  const result = scenario.latest_result;

  if (!result) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-8">
        <div className="text-center space-y-3 max-w-sm">
          <p className="text-sm text-[var(--foreground-muted)]">
            No run yet for this scenario.
          </p>
          <button
            type="button"
            onClick={() => {
              lab.setSelectedIds([scenario.id]);
              lab.handleRunSelected();
            }}
            disabled={lab.isRunning}
            className="px-3 py-2 text-sm bg-[var(--accent)] text-[var(--accent-foreground)] rounded-md hover:bg-[var(--accent-hover)] disabled:opacity-50 inline-flex items-center gap-2"
          >
            <HiPlay className="w-4 h-4" />
            {lab.isRunning ? "Running…" : "Run this scenario"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 py-5 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Chat transcript</h4>
        <span className="text-xs text-[var(--foreground-muted)]">
          {result.turns.length} turns
        </span>
      </div>
      <ChatTranscript
        messages={result.turns}
        userLabel={scenario.persona_name || "Simulated user"}
        assistantLabel="Agent"
        maxHeightClassName="max-h-[640px]"
      />
    </div>
  );
};

const VerdictTab: React.FC<{ lab: UseAgentTestLab }> = ({ lab }) => {
  const scenario = lab.selectedScenario!;
  const result = scenario.latest_result;

  if (!result) {
    return (
      <div className="px-5 py-8 text-sm text-[var(--foreground-muted)] text-center">
        Run this scenario to see the judge's verdict.
      </div>
    );
  }

  return (
    <div className="px-5 py-5 space-y-5">
      <div className="flex items-center justify-between rounded-lg border border-[var(--border)] bg-[var(--card-container)] px-4 py-3">
        <div>
          <div className="text-xs text-[var(--foreground-muted)] uppercase tracking-wide">
            Score
          </div>
          <div className="text-2xl font-bold tabular-nums">
            {formatPercent(result.score)}
          </div>
        </div>
        <span
          className={`px-3 py-1 rounded-full border text-xs capitalize ${getStatusClasses(
            result.status
          )}`}
        >
          {statusLabel(result.status)}
        </span>
      </div>

      {result.critical_failure && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3">
          <HiXCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">Critical failure</div>
            {result.failure_reason && (
              <p className="mt-1 text-red-700/90">{result.failure_reason}</p>
            )}
          </div>
        </div>
      )}

      {result.criteria_results.length > 0 && (
        <section className="space-y-2">
          <h4 className="text-sm font-medium">Pass criteria</h4>
          <ul className="space-y-1.5">
            {result.criteria_results.map((criterion) => (
              <li
                key={criterion.criterion}
                className="flex items-start gap-2 text-sm rounded-md px-2 py-1.5 hover:bg-[var(--muted)]/50"
              >
                {criterion.passed ? (
                  <HiCheck className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <HiX className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className={criterion.passed ? "" : "text-red-700"}>
                    {criterion.criterion}
                  </div>
                  {criterion.notes && (
                    <div className="text-xs text-[var(--foreground-muted)] mt-0.5">
                      {criterion.notes}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {result.fixes_needed.length > 0 && (
        <section className="space-y-2">
          <h4 className="text-sm font-medium">Fixes needed</h4>
          <ul className="space-y-1.5">
            {result.fixes_needed.map((fix) => (
              <li
                key={fix}
                className="text-sm bg-amber-50 text-amber-800 border-l-2 border-amber-400 px-3 py-2 rounded-r-md"
              >
                {fix}
              </li>
            ))}
          </ul>
        </section>
      )}

      {result.judge_notes && (
        <section className="space-y-2">
          <h4 className="text-sm font-medium">Judge notes</h4>
          <p className="text-sm text-[var(--foreground-muted)] whitespace-pre-wrap">
            {result.judge_notes}
          </p>
        </section>
      )}

      {!result.critical_failure &&
        result.criteria_results.length === 0 &&
        result.fixes_needed.length === 0 &&
        !result.judge_notes &&
        result.failure_reason && (
          <p className="text-sm text-red-700">{result.failure_reason}</p>
        )}

      {result.criteria_results.every((c) => c.passed) &&
        !result.critical_failure && (
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-100 rounded-md p-3">
            <HiCheckCircle className="w-4 h-4" />
            All pass criteria met.
          </div>
        )}
    </div>
  );
};
